import { isSelectionRequired } from "@workspace/model"
import type {
  MealDealMenu,
  MealDealStep,
  MenuItem,
  ModifierGroup,
  ModifierOption,
  SelectionRule,
} from "@workspace/model"

/**
 * Sold-out at item + modifier level (US-021 / S-P1-5).
 *
 * A plain shared module (not a route), consumed by Server Components (menu
 * list card, item header), the client `ModifierCustomizer`, cart add
 * actions, AND `lib/order-totals.ts` (US-034's checkout re-validation) so
 * item- and option-level availability are resolved identically everywhere,
 * never re-derived ad hoc. Lives in `lib/` (rather than under `app/menu/`)
 * because it's now also a dependency of checkout, not just the menu routes.
 */

/** All options in a group, including subgroup options (flat, for rule checks). */
export function allGroupOptions(group: ModifierGroup): ModifierOption[] {
  return [
    ...(group.options ?? []),
    ...(group.subgroups?.flatMap((sub) => sub.options) ?? []),
  ]
}

/**
 * required/optional: `required` (single OR multi, contract v0.28.0 — a
 * noSelect "None" counts as the pick), or a positive `min`. Same rule the
 * selection engine gates Add with (`isSelectionRequired`, @workspace/model).
 */
export function isRequiredRule(rule: SelectionRule): boolean {
  return isSelectionRequired(rule)
}

export type ItemUnavailableReason = "sold_out" | "required_group_unavailable"

export interface ItemAvailability {
  available: boolean
  reason: ItemUnavailableReason | null
  /** Only set when reason === "required_group_unavailable". */
  unavailableGroupTitle?: string
}

/**
 * Resolves whether an item is orderable:
 *  1. Item-level `available: false` ⇒ sold out (already the item's own flag).
 *  2. Otherwise, cascade: if a REQUIRED modifier group has zero available
 *     options left (every option, including subgroup options, is
 *     `available: false`), the diner can never complete that required
 *     selection, so the item as a whole becomes unavailable — with a clear,
 *     specific reason (which group) rather than a generic message.
 * Optional groups going fully unavailable do NOT block the item (the diner
 * can simply skip an optional group).
 */
export function resolveItemAvailability(item: MenuItem): ItemAvailability {
  if (!item.available) {
    return { available: false, reason: "sold_out" }
  }

  for (const group of item.modifierGroups ?? []) {
    if (!isRequiredRule(group.selection)) continue
    const options = allGroupOptions(group)
    if (options.length > 0 && options.every((option) => !option.available)) {
      return {
        available: false,
        reason: "required_group_unavailable",
        unavailableGroupTitle: group.title,
      }
    }
  }

  return { available: true, reason: null }
}

/**
 * Whether ONE meal-deal pick is orderable. The pick resolves the way
 * `priceMealDeal` (@workspace/ordering) resolves it: a misc item
 * (`MealDealStep.miscItems`) by its own `available` flag; otherwise the menu
 * item it names, which must pass {@link resolveItemAvailability} AND — in a
 * standard course — not be withdrawn by its config row (`MealDealItem.available`).
 *
 * The wizard marks sold-out tiles with this, and the add-to-cart action and
 * `/cart`/`/checkout` re-check every submitted pick with it, so a crafted or
 * stale selection can't order what the wizard shows as sold out.
 */
export function isMealDealPickAvailable(
  step: MealDealStep,
  itemId: string,
  itemsById: ReadonlyMap<string, MenuItem>
): boolean {
  const misc = step.miscItems?.find((m) => m.id === itemId)
  if (misc) return misc.available !== false
  const row = step.items?.find(
    (r) => r.itemId === itemId && (r.menuType ?? "variety") === "variety"
  )
  if (row?.available === false) return false
  const item = itemsById.get(itemId)
  return item !== undefined && resolveItemAvailability(item).available
}

/** Every pick of a meal-deal selection is orderable (unknown steps are ignored, as the engine drops them). */
export function areMealDealPicksAvailable(
  deal: MealDealMenu,
  selections: readonly { stepId: string; itemId: string }[],
  itemsById: ReadonlyMap<string, MenuItem>
): boolean {
  return selections.every((selection) => {
    const step = deal.steps.find((s) => s.id === selection.stepId)
    return !step || isMealDealPickAvailable(step, selection.itemId, itemsById)
  })
}

/**
 * Client-safe mirror of `resolveGroupDefaultSet` (@workspace/ordering's
 * modifiers.ts) — the engine is server-only by call-site discipline, so the
 * customizers (all `"use client"`) read the identical rule from here.
 *
 * `group.defaultOptionIds` (port of Dart `ItemOptionVarietyConfig
 * .defaultOptionIds`, item_options.dart) is authoritative when present, so an
 * empty array means "nothing pre-selected"; otherwise the per-option
 * `defaultSelected` flags decide. Only the group's own available options
 * survive, in option order, and a single-select group keeps at most one.
 * Keep this in lockstep with the engine copy.
 */
export function resolveGroupDefaultSet(group: ModifierGroup): Set<string> {
  const options = allGroupOptions(group).filter((option) => option.available)
  const configured = group.defaultOptionIds
  const ids = configured
    ? options
        .filter((option) => configured.includes(option.id))
        .map((option) => option.id)
    : options.filter((option) => option.defaultSelected).map((o) => o.id)
  return new Set(group.selection.kind === "single" ? ids.slice(0, 1) : ids)
}
